import React from "react";

// Shared chat helpers used by both the admin support console (ChatPanel) and
// the dealer inbox (DealerChat), so the two web surfaces behave identically to
// the mobile app.

// Lead to inbox hand-off. Reply / Chat on a lead (Leads, Listings, the
// Dashboard) queues the tenant here, then switches to the inbox, which opens
// that chat (DealerChat). Keyed by the tenant's uid: names aren't unique.
const LEAD_CHAT_KEY = "gs-lead-chat";

export function queueLeadChat(lead) {
  try {
    localStorage.setItem(LEAD_CHAT_KEY, JSON.stringify({ peerId: lead.tenantId || "", name: lead.tenantName || "" }));
  } catch {
    // Storage blocked: the inbox still opens, just without a preselected chat.
  }
}

/** The queued tenant as { peerId, name }, or null. Clears the queue either way. */
export function takeQueuedLeadChat() {
  try {
    const raw = localStorage.getItem(LEAD_CHAT_KEY);
    localStorage.removeItem(LEAD_CHAT_KEY);
    const queued = raw ? JSON.parse(raw) : null;
    return queued && queued.peerId ? queued : null;
  } catch {
    return null;
  }
}

export function mediaLabelFor(mediaType) {
  if (mediaType === "audio") return "🎤 Voice message";
  if (mediaType === "image") return "📷 Photo";
  if (mediaType === "video") return "🎬 Video";
  if (mediaType === "document") return "📄 Document";
  return "Attachment";
}

export function formatFileSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Reply payload written onto an outgoing message, matching the mobile schema
// (see chat-conversation.tsx buildReplyPayload): the quote shows who was
// replied to and a short preview of their message.
export function buildReplyPayload(msg, myId, peerName) {
  const mine = (msg.senderId || msg.sender) === myId;
  return {
    id: msg.id,
    text: msg.text || "",
    senderId: msg.senderId || msg.sender || "",
    senderName: mine ? "You" : (peerName || "User"),
    mediaType: msg.mediaType || null,
  };
}

// Soft "delete for everyone": rules only let admins hard-delete message docs,
// so — like the mobile app — we blank the content and flag it. Peers render a
// "This message was deleted" tombstone.
export const SOFT_DELETE_FIELDS = {
  text: "",
  mediaUrl: null,
  mediaUrls: null,
  mediaType: null,
  mediaName: null,
  durationMillis: null,
  replyTo: null,
  deleted: true,
};

// Matches http(s) URLs and bare www. domains, mirroring the mobile linkifier.
const URL_RE = /((?:https?:\/\/|www\.)[^\s]+)/gi;

export function firstUrlIn(text) {
  if (!text) return null;
  const m = text.match(/(https?:\/\/[^\s]+)/i);
  return m ? m[0] : null;
}

// Render message text with clickable links (parity with mobile's tappable
// URLs). Returns an array of strings + <a> nodes safe to drop into JSX.
export function renderTextWithLinks(text) {
  if (!text) return text;
  const parts = text.split(URL_RE);
  return parts.map((part, i) => {
    if (!part) return null;
    if (URL_RE.test(part)) {
      URL_RE.lastIndex = 0; // reset because we reuse the global regex
      const href = part.startsWith("http") ? part : `https://${part}`;
      return (
        <a key={i} href={href} target="_blank" rel="noreferrer" className="chat-inline-link">
          {part}
        </a>
      );
    }
    return part;
  });
}
